import { useContext, useEffect, useState } from "react";
import { Modal, Form, message } from "antd";
import { AuthContext, DeliveryContext } from "@CONTEXTS";
import { HJCreateDelivery } from "@FORMS";
import { updateDelivery } from "@DELIVERIES";
import { mapProductsForApi, normalizeDeliveryProducts } from "@HELPERS";

const UpdateModal = () => {
  const { updateModal, dispatch, mutate } = useContext(DeliveryContext);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [initValues, setInitValues] = useState({});
  const { info } = useContext(AuthContext);

  useEffect(() => {
    let obj = {
      customerDeliveryNo: updateModal.customerDeliveryNo,
      recipientName: updateModal.recipient.firstName,
      recipientSurname: updateModal.recipient.lastName,
      recipientPhone: updateModal.recipient.phone,
      desi: updateModal?.desi,
      manuelDesi: updateModal?.manuelDesi,
      deliveryAmount: updateModal?.deliveryAmount,
      paymentType: updateModal?.paymentType,
      deliveryType: updateModal?.deliveryType?.type || "HX_STD",
      slot: updateModal?.deliveryType?.slot,
      totalParcels: updateModal?.totalParcels,
      recipientCity: updateModal?.recipient?.address?.city?.name,
      recipientTown: updateModal?.recipient?.address?.town?.name,
      recipientDistrict: updateModal?.recipient?.address?.district?.name,
      recipientAddressLine: updateModal.recipient.address.addressLine,
      receiverName: updateModal?.receiver?.firstName,
      receiverSurname: updateModal?.receiver?.lastName,
      receiverPhone: updateModal?.receiver?.phone,
      warehouse: updateModal?.warehouse?.warehouseId,
      products: normalizeDeliveryProducts(updateModal),
    };

    if (info?.customFields) {
      for (let i = 0; i < updateModal.customFields?.length; i++) {
        const customItem = updateModal.customFields[i];

        const customField = info.customFields.find(
          (x) => x.name === customItem.name
        );

        if (customField?.type === "FILE_UPLOADER") {
          obj[customItem.name] = customItem.value
            ? [
                {
                  uid: "-1", // Sabit bir ID
                  name: customItem.originalFileName, // Orijinal ismi DB'de tutmuyorsan varsayılan isim
                  status: "done",
                  apiUrl: `deliveries/${updateModal.customerDeliveryNo}/preview/${customItem._id}`, // Linki içine gömüyoruz
                  oldValue: customItem.value,
                },
              ]
            : [];
        } else {
          obj[customItem.name] = customItem.value;
        }
      }
    }
    // objyi initValues'e eklememizin sebebi, eksik kalan form elemanlarını doldurmak.
    // örn: deliveryType eski gönderilerde olmayacak ve updateModal içerisinde null gelecektir.
    // o yüzden direk updateModal içeriğini kullanmak yerine, her şeyi obj den yönetiyoruz
    setInitValues(obj);
    form.setFieldsValue({
      delivery: obj,
    });
  }, [updateModal, form]);

  const handleUpdate = async (values) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("customerDeliveryNo", values.customerDeliveryNo || null);
    formData.append("warehouse", values.warehouse || null);
    formData.append("desi", values.desi || null);
    formData.append("manuelDesi", values.manuelDesi || null);
    formData.append("totalParcels", values.totalParcels || null);
    formData.append("deliveryType", values.deliveryType || null);
    formData.append("slot", values.slot || null);
    formData.append("deliveryAmount", values.deliveryAmount || null);
    formData.append("paymentType", values.paymentType || null);
    formData.append("products", mapProductsForApi(values?.products));

    if (values.slot) formData.append("slot", values.slot || null);

    formData.append(
      "recipient",
      JSON.stringify({
        firstName: values.recipientName,
        lastName: values.recipientSurname,
        phone: values.recipientPhone.replace(/ /g, ""),
        address: {
          addressLine: values.recipientAddressLine,
          city: {
            id: values.recipientCity?.id,
            name: values.recipientCity.name,
          },
          town: {
            id: values.recipientTown?.id,
            name: values.recipientTown.name,
          },
          district: values.recipientDistrict
            ? {
                id: values.recipientDistrict?.id,
                name: values.recipientDistrict.name,
              }
            : null,
        },
      }) || null
    );
    if (values.receiverName) {
      formData.append(
        "receiver",
        JSON.stringify({
          firstName: values.receiverName,
          lastName: values.receiverSurname,
          phone: values.receiverPhone.replace(/ /g, ""),
        }) || null
      );
    }

    if (info?.customFields) {
      const customFields = info?.customFields?.filter((x) =>
        x?.deliveryType?.includes("DELIVERY")
      );

      const cfx = [];

      for (let i = 0; i < customFields.length; i++) {
        const customItem = customFields[i];

        cfx.push({
          name: customItem.name,
          value: values[customItem.name],
        });

        if (customItem.type === "FILE_UPLOADER") {
          if (
            (!values[customItem.name] ||
              values[customItem.name]?.length < 1 ||
              !values[customItem.name]?.[0]?.originFileObj) &&
            customItem.required
          ) {
            setLoading(false);
            return message.error({
              content: `${customItem.label} için geçersiz dosya.`,
              className: "app-error-message",
            });
          }
          formData.append(
            customItem.name,
            values[customItem.name]?.[0]?.originFileObj
          );
        }
      }

      if (cfx.length > 0) {
        formData.append("customFields", JSON.stringify(cfx));
      }
    }

    const response = await updateDelivery(formData, updateModal._id);

    if (response.customerDeliveryNo) {
      message.success({
        content: "Gönderi başarıyla güncellendi",
        className: "app-success-message",
      });
      mutate();
      setTimeout(() => {
        dispatch({ type: "SET_UPDATE_MODAL", payload: false });
      }, 750);
    } else {
      setLoading(false);
      message.error({
        content:
          response.response?.data?.error?.msg || response.response?.data?.data,
        className: "app-error-message",
      });
    }
  };

  return (
    <Modal
      visible
      title={`${updateModal.customerDeliveryNo} - Gönderi Düzenle`}
      width={1100}
      centered
      onCancel={() => {
        if (!loading) {
          dispatch({ type: "SET_UPDATE_MODAL", payload: false });
        }
      }}
      confirmLoading={loading}
      onOk={() => {
        form.validateFields().then((values) => {
          handleUpdate(values.delivery);
        });
      }}
    >
      <Form form={form} layout="vertical">
        <HJCreateDelivery
          form={form}
          fieldName="delivery"
          initValues={initValues}
          warehouseSelect
        />
      </Form>
    </Modal>
  );
};

export default UpdateModal;
